import { useMemo, useState, useTransition } from "react";
import { Alert, AlertDescription, AlertTitle } from "../../alert";
import { Button } from "../../button";
import { Card } from "../../card";
import { Checkbox } from "../../checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../dialog";
import { formatRelativeTime } from "../../format";
import { DataTable, type DataTableColumn } from "../data-table";
import { SectionTitle } from "../section-title";
import { StatCard } from "../stat-card";
import { IntentBadge } from "../status-badge";
import type { Intent } from "../../tokens";
import { useApi, apiCall } from "../../api/client";
import {
  dodPredicates,
  type DodPredicateListDto,
  type DodPredicateRowDto,
} from "../../api/contracts/database/dod-predicates";

/**
 * Any-workflow DoD predicates (#46035). Shared by `/database/views` and
 * `/database/objects`. Proposal vs confirmed is the binding line; DEPENDENT vs
 * INDEPENDENT is Amendment A2. Confirm is an explicit dialog and, on a live
 * task, an extra checkbox — this UI never silently `dod_confirm`s.
 */
export function DodPredicatesTable({
  entityKind,
  viewUid,
}: {
  entityKind?: string;
  viewUid?: string;
}) {
  const query = {
    ...(entityKind ? { entity_kind: entityKind } : {}),
    ...(viewUid ? { view_uid: viewUid } : {}),
  };
  const { data, error, isLoading, mutate } = useApi(dodPredicates.list, { query });
  const [pendingUid, setPendingUid] = useState<string | null>(null);

  if (error) {
    return (
      <Card className="p-6">
        <SectionTitle variant="plain" title="Predicates" />
        <p className="mt-3 type-body text-destructive">{error.message ?? String(error)}</p>
      </Card>
    );
  }
  if ((isLoading && !data) || !data) {
    return (
      <Card className="p-6">
        <SectionTitle variant="plain" title="Predicates" />
        <p className="mt-3 type-body text-muted-foreground">Loading predicates…</p>
      </Card>
    );
  }
  if (!data.readable) {
    return (
      <Card className="p-6">
        <SectionTitle variant="plain" title="Predicates" />
        <Alert className="mt-3">
          <AlertTitle>Treat this as UNKNOWN</AlertTitle>
          <AlertDescription>
            Predicates could not be read
            {data.unreadableReason ? ` — ${data.unreadableReason}` : ""}. That is
            not the same as a vault with no derived DoD.
          </AlertDescription>
        </Alert>
      </Card>
    );
  }

  return (
    <Card className="space-y-5 p-6">
      <SectionTitle
        variant="plain"
        title="Predicates"
        subtitle="Derived Definition of Done. A proposal (source=derived, unbound) does not gate done. Confirmed criteria do."
        count={data.predicates.length}
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
        <StatCard title="Proposals" value={String(data.proposalCount)} description="Unbound — do not gate done" />
        <StatCard title="Confirmed" value={String(data.confirmedCount)} description="Gate done" />
        <StatCard title="Dependent" value={String(data.dependentCount)} description="Rows the agent can write" />
        <StatCard title="Independent" value={String(data.independentCount)} description="oracle: refs only" />
      </div>

      {data.predicates.length > 0 && data.independentCount === 0 ? (
        <Alert>
          <AlertTitle>Every currently-derived predicate is DEPENDENT</AlertTitle>
          <AlertDescription>
            Field-present saved views measure Golden Schema rows the agent can
            write. Independent oracles (git tree, gate-receipt binding, two-agent
            identity) mint as <code className="font-mono">oracle:</code> refs and
            will appear here when a ship-verify derive lands them.
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="-mx-6">
        <PredicateRows
          rows={data.predicates}
          pendingUid={pendingUid}
          onAskConfirm={setPendingUid}
        />
      </div>

      {pendingUid ? (
        <ConfirmPredicateDialog
          row={data.predicates.find((r) => r.uid === pendingUid) ?? null}
          onClose={() => setPendingUid(null)}
          onConfirmed={async (list) => {
            setPendingUid(null);
            await mutate(list, { revalidate: false });
          }}
        />
      ) : null}
    </Card>
  );
}

function PredicateRows({
  rows,
  pendingUid,
  onAskConfirm,
}: {
  rows: DodPredicateRowDto[];
  pendingUid: string | null;
  onAskConfirm: (uid: string) => void;
}) {
  const columns = useMemo<DataTableColumn<DodPredicateRowDto>[]>(
    () => [
      {
        key: "predicate",
        header: "Predicate",
        render: (r) => (
          <div className="flex flex-col">
            <span className="font-medium text-foreground">
              {r.viewName ?? r.ref ?? r.uid}
            </span>
            {r.why ? (
              <span className="type-subtext text-muted-foreground">{r.why}</span>
            ) : null}
          </div>
        ),
      },
      {
        key: "kind",
        header: "Object",
        render: (r) => (
          <span className="font-mono type-subtext text-muted-foreground">
            {r.entityKind ?? "—"}
          </span>
        ),
      },
      {
        key: "stance",
        header: "Stance",
        render: (r) => (
          <IntentBadge intent={stanceIntent(r.stance)}>
            {r.stance === "proposal" ? "Proposal" : "Confirmed"}
          </IntentBadge>
        ),
      },
      {
        key: "independence",
        header: "Independence",
        render: (r) => (
          <IntentBadge intent={independenceIntent(r.independence)}>
            {r.independence === "independent"
              ? "INDEPENDENT"
              : r.independence === "dependent"
                ? "DEPENDENT"
                : "Unknown"}
          </IntentBadge>
        ),
      },
      {
        key: "task",
        header: "Task",
        render: (r) => (
          <div className="flex flex-col">
            <span className="type-body text-foreground">#{r.taskId}</span>
            <span className="type-subtext text-muted-foreground">{r.taskTitle}</span>
          </div>
        ),
      },
      {
        key: "bound",
        header: "Bound",
        render: (r) => (
          <span className="text-muted-foreground">
            {r.boundAt ? formatRelativeTime(r.boundAt) : "—"}
          </span>
        ),
      },
      {
        key: "action",
        header: "Action",
        render: (r) =>
          r.stance === "proposal" ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onAskConfirm(r.uid)}
              disabled={pendingUid === r.uid}
            >
              Confirm…
            </Button>
          ) : (
            <span className="type-subtext text-muted-foreground">Gates done</span>
          ),
      },
    ],
    [onAskConfirm, pendingUid],
  );

  return (
    <DataTable
      wrapInCard={false}
      rows={rows}
      columns={columns}
      rowKey={(r) => r.uid}
      empty="No derived view or oracle predicates yet. dod_derive mints them as unbound proposals."
    />
  );
}

function ConfirmPredicateDialog({
  row,
  onClose,
  onConfirmed,
}: {
  row: DodPredicateRowDto | null;
  onClose: () => void;
  onConfirmed: (list: DodPredicateListDto) => Promise<void>;
}) {
  const [acked, setAcked] = useState(false);
  const [pending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);

  if (!row) return null;

  const needsAck = row.liveTask;
  const canSubmit = !needsAck || acked;

  function submit() {
    if (!row) return;
    setError(null);
    startTransition(async () => {
      try {
        const res = await apiCall(dodPredicates.confirm, {
          body: {
            criterion: row.uid,
            ...(needsAck ? { acknowledgeForeignLive: true } : {}),
          },
        });
        if (!res.ok) {
          setError(res.reason ?? "refused");
          return;
        }
        await onConfirmed(res.list);
      } catch (e) {
        setError((e as Error).message);
      }
    });
  }

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Confirm this predicate?</DialogTitle>
          <DialogDescription>
            Confirming stamps <code className="font-mono">bound_at</code> so this
            criterion starts gating <code className="font-mono">done</code> on
            #{row.taskId} ({row.taskTitle}). A proposal does not gate until you
            do this.
          </DialogDescription>
        </DialogHeader>
        {needsAck ? (
          <label className="flex items-start gap-2 type-body">
            <Checkbox
              checked={acked}
              onCheckedChange={(v) => setAcked(v === true)}
              disabled={pending}
            />
            <span>
              I understand this gates <code className="font-mono">done</code> on
              another agent&apos;s live task
              {row.lastInProgressAssignee || row.taskAssignee
                ? ` (${row.lastInProgressAssignee ?? row.taskAssignee}, ${row.taskStatus})`
                : ` (${row.taskStatus})`}
              .
            </span>
          </label>
        ) : null}
        {error ? <p className="type-body text-destructive">{error}</p> : null}
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button type="button" variant="accept" onClick={submit} disabled={pending || !canSubmit}>
            {pending ? "Confirming…" : "Confirm predicate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function stanceIntent(stance: DodPredicateRowDto["stance"]): Intent {
  return stance === "confirmed" ? "success" : "warning";
}

function independenceIntent(independence: DodPredicateRowDto["independence"]): Intent {
  if (independence === "independent") return "info";
  if (independence === "dependent") return "neutral";
  return "warning";
}

export function viewPredicateSummary(
  viewUid: string,
  rows: DodPredicateRowDto[],
): { stance: DodPredicateRowDto["stance"]; independence: DodPredicateRowDto["independence"]; count: number } | null {
  const hit = rows.filter((r) => r.viewUid === viewUid);
  if (hit.length === 0) return null;
  const hasProposal = hit.some((r) => r.stance === "proposal");
  const allDependent = hit.every((r) => r.independence === "dependent");
  const allIndependent = hit.every((r) => r.independence === "independent");
  return {
    stance: hasProposal ? "proposal" : "confirmed",
    independence: allIndependent ? "independent" : allDependent ? "dependent" : "unknown",
    count: hit.length,
  };
}
